import { LOOK } from "./dev";
import type { Rgb } from "./tracks";

// The colours being tuned in the visual revamp (REVAMP_LOG.md), in one place:
// each map's ground, road, walls and sky, and the light. Colours are display
// (sRGB) values, 0…1, written straight to the screen — the shaders do no
// colour-space conversion or tone mapping. `?look=old` draws the colours from
// before the revamp.

/** `sky` is the horizon (and the fog); `skyTop` the zenith; `hills` the near and far rings of hills round the world. */
type Colors = { ground: Rgb; road: Rgb; wall: Rgb; wallTop: Rgb; sky: Rgb; skyTop?: Rgb; hills?: [Rgb, Rgb] };

const OLD: Record<"forest" | "meadow" | "snow", Colors> = {
  forest: { ground: [0.3, 0.46, 0.22], road: [0.56, 0.42, 0.28], wall: [0.46, 0.3, 0.17], wallTop: [0.8, 0.64, 0.42], sky: [0.66, 0.81, 0.9] },
  meadow: { ground: [0.47, 0.74, 0.33], road: [0.55, 0.42, 0.28], wall: [0.64, 0.52, 0.37], wallTop: [0.7, 0.58, 0.42], sky: [0.6, 0.83, 0.98] },
  // Packed snow, a shade darker and bluer than the fresh snow either side, so the road reads.
  snow: { ground: [0.95, 0.97, 1], road: [0.66, 0.72, 0.82], wall: [0.62, 0.82, 0.95], wallTop: [0.82, 0.93, 1], sky: [0.72, 0.84, 0.96] },
};

const NEW: typeof OLD = {
  forest: {
    ground: [0.24, 0.56, 0.2], road: [0.8, 0.56, 0.31], wall: [0.5, 0.31, 0.15], wallTop: [0.88, 0.68, 0.42],
    sky: [0.75, 0.9, 1], skyTop: [0.18, 0.55, 1], hills: [[0.13, 0.42, 0.24], [0.42, 0.62, 0.72]],
  },
  meadow: {
    ground: [0.42, 0.78, 0.24], road: [0.84, 0.6, 0.34], wall: [0.72, 0.68, 0.62], wallTop: [0.84, 0.8, 0.74],
    sky: [0.75, 0.9, 1], skyTop: [0.23, 0.63, 1], hills: [[0.36, 0.66, 0.3], [0.5, 0.72, 0.8]],
  },
  snow: {
    ground: [0.97, 0.98, 1], road: [0.64, 0.74, 0.9], wall: [0.55, 0.8, 0.97], wallTop: [0.82, 0.94, 1],
    sky: [0.78, 0.9, 1], skyTop: [0.2, 0.55, 0.98], hills: [[0.86, 0.91, 0.98], [0.66, 0.78, 0.92]],
  },
};

/**
 * The revamp's light (the old look's is fixed in the shaders): the
 * hemisphere's sky, half white and half #9fd4ff, at half strength; its ground,
 * #c99a62 at a third. The sun, warm white, is added in the shaders.
 */
export const HEMISPHERE = { sky: [0.405, 0.457, 0.5] as Rgb, ground: [0.28, 0.21, 0.13] as Rgb };

/** Each map's colours, by its key. */
export const MAP_COLORS = LOOK === "old" ? OLD : NEW;
